import { z } from 'zod';
import { CONDICIONES_PAGO, ESTADOS_FACTURA_MANUALES, ESTADOS_PAGO_GASTO, MEDIOS_PAGO, PERIODICIDADES, TIPOS_CATEGORIA_GASTO, TIPOS_DOCUMENTO_GASTO, TIPOS_DOCUMENTO_VENTA, } from '../dominio/enums.js';
import { enumDe, fechaOpcional, fechaSchema, idSchema, montoOpcional, montoSchema, paginacionSchema, porcentajeSchema, textoOpcional, textoRequerido, } from './comunes.js';
// ─────────────────────────────────────────────────────────────
// Categorías de gasto y proveedores
// ─────────────────────────────────────────────────────────────
export const crearCategoriaGastoSchema = z.object({
    nombre: textoRequerido(100, 'El nombre de la categoría'),
    tipo: enumDe(TIPOS_CATEGORIA_GASTO).default('DIRECTO'),
    esCostoPersonal: z.boolean().default(false),
    activa: z.boolean().default(true),
});
export const actualizarCategoriaGastoSchema = crearCategoriaGastoSchema.partial();
export const crearProveedorSchema = z.object({
    nombre: textoRequerido(200, 'El nombre del proveedor'),
    rut: z.string().trim().max(20).optional(),
    contacto: textoOpcional(200),
    email: z.string().trim().email('Correo inválido').optional().or(z.literal('')),
    telefono: textoOpcional(50),
    notas: textoOpcional(2000),
});
// ─────────────────────────────────────────────────────────────
// Gastos
// ─────────────────────────────────────────────────────────────
/**
 * Un gasto puede o no pertenecer a un proyecto.
 *
 * Sin `proyectoId` es gasto general de la empresa —contabilidad, arriendo,
 * licencias base—, que el CRM original no podía registrar en ninguna parte. Con
 * `proyectoId` es costo directo e imputa contra el presupuesto de ese proyecto.
 */
export const crearGastoBaseSchema = z
    .object({
    proyectoId: idSchema.optional().nullable(),
    /** Selector de 1 clic: 'PROYECTO' (costo directo) o 'GENERAL' (estructura de empresa) */
    ambito: z.enum(['PROYECTO', 'GENERAL']).optional(),
    categoriaGastoId: idSchema,
    proveedorId: idSchema.optional().nullable(),
    proveedorNombre: textoOpcional(200),
    proveedorRut: textoOpcional(20),
    fecha: fechaSchema,
    concepto: textoRequerido(300, 'El concepto del gasto'),
    /** Monto neto. Es el costo real: el IVA de compra es recuperable. */
    montoNeto: montoSchema,
    ivaPct: porcentajeSchema.optional(),
    /** Alternativa al neto cuando solo se tiene el total del documento. */
    montoTotal: montoOpcional,
    tipoDocumento: enumDe(TIPOS_DOCUMENTO_GASTO).default('FACTURA'),
    numeroDocumento: textoOpcional(60),
    estadoPago: enumDe(ESTADOS_PAGO_GASTO).default('PENDIENTE'),
    fechaPago: fechaOpcional,
    notas: textoOpcional(2000),
});
export const crearGastoSchema = crearGastoBaseSchema.refine((datos) => datos.montoNeto > 0 || (datos.montoTotal ?? 0) > 0, {
    message: 'Ingresa el monto neto o el monto total del gasto',
    path: ['montoNeto'],
});
export const actualizarGastoSchema = z.object({
    proyectoId: idSchema.optional().nullable(),
    categoriaGastoId: idSchema.optional(),
    proveedorId: idSchema.optional().nullable(),
    proveedorNombre: textoOpcional(200),
    fecha: fechaOpcional,
    concepto: textoRequerido(300, 'El concepto').optional(),
    montoNeto: montoSchema.optional(),
    ivaPct: porcentajeSchema.optional(),
    tipoDocumento: enumDe(TIPOS_DOCUMENTO_GASTO).optional(),
    numeroDocumento: textoOpcional(60),
    estadoPago: enumDe(ESTADOS_PAGO_GASTO).optional(),
    fechaPago: fechaOpcional,
    notas: textoOpcional(2000),
});
export const filtrosGastoSchema = paginacionSchema.extend({
    busqueda: z.string().trim().max(200).optional(),
    proyectoId: idSchema.optional(),
    categoriaGastoId: idSchema.optional(),
    proveedorId: idSchema.optional(),
    proveedorNombre: z.string().trim().max(200).optional(),
    estadoPago: enumDe(ESTADOS_PAGO_GASTO).optional(),
    desde: fechaOpcional,
    hasta: fechaOpcional,
    /** `true` para traer solo gastos generales (sin proyecto asociado). */
    soloGeneral: z.coerce.boolean().optional(),
    /** Selector rápido de 1 clic: 'TODOS' | 'PROYECTO' | 'GENERAL' */
    ambito: z.enum(['TODOS', 'PROYECTO', 'GENERAL']).optional(),
    /** `true` para filtrar cuentas por pagar pendientes de transferencia */
    soloPendientes: z.coerce.boolean().optional(),
});
/** Acción rápida de 1 clic para marcar gasto pagado en la fila */
export const pagarGastoSchema = z.object({
    fechaPago: fechaOpcional,
    medioPago: enumDe(MEDIOS_PAGO).optional(),
    referencia: textoOpcional(100),
});
/** Plantilla de gasto que se repite: hosting, sueldos, licencias. */
export const crearGastoRecurrenteSchema = z.object({
    descripcion: textoRequerido(300, 'La descripción'),
    categoriaGastoId: idSchema,
    proveedorId: idSchema.optional().nullable(),
    proveedorNombre: textoOpcional(200),
    proyectoId: idSchema.optional().nullable(),
    montoNeto: montoSchema,
    ivaPct: porcentajeSchema.optional(),
    periodicidad: enumDe(PERIODICIDADES).default('MENSUAL'),
    proximaFecha: fechaSchema,
    fechaFin: fechaOpcional,
    activo: z.boolean().default(true),
});
// ─────────────────────────────────────────────────────────────
// Facturas
// ─────────────────────────────────────────────────────────────
/**
 * Emisión de una factura.
 *
 * `fechaVencimiento` **no se recibe**: se calcula desde `fechaEmision` y
 * `condicionPago`. Dejar que el cliente la mande abriría la puerta a que
 * vencimiento y condición se contradigan, que es justo el tipo de inconsistencia
 * que este rediseño busca eliminar.
 */
export const crearFacturaSchema = z
    .object({
    proyectoId: idSchema.optional().nullable(),
    organismoId: idSchema,
    /** Cuota del cronograma que esta factura materializa, si viene de una. */
    cuotaId: idSchema.optional().nullable(),
    hitoId: idSchema.optional().nullable(),
    numero: textoOpcional(60),
    folioSii: textoOpcional(60),
    tipoDocumento: enumDe(TIPOS_DOCUMENTO_VENTA).default('FACTURA_ELECTRONICA'),
    fechaEmision: fechaSchema,
    condicionPago: enumDe(CONDICIONES_PAGO).default('DIAS_30'),
    montoNeto: montoSchema,
    ivaPct: porcentajeSchema.optional(),
    estado: enumDe(ESTADOS_FACTURA_MANUALES).default('EMITIDA'),
    numeroOrdenCompra: textoOpcional(60),
    notas: textoOpcional(2000),
})
    .refine((datos) => datos.montoNeto > 0, {
    message: 'El monto neto de la factura debe ser mayor a cero',
    path: ['montoNeto'],
});
export const actualizarFacturaSchema = z.object({
    numero: textoOpcional(60),
    folioSii: textoOpcional(60),
    fechaEmision: fechaOpcional,
    condicionPago: enumDe(CONDICIONES_PAGO).optional(),
    montoNeto: montoSchema.optional(),
    ivaPct: porcentajeSchema.optional(),
    estado: enumDe(ESTADOS_FACTURA_MANUALES).optional(),
    numeroOrdenCompra: textoOpcional(60),
    notas: textoOpcional(2000),
});
/**
 * Registro de un pago.
 *
 * Los pagos son la única fuente de verdad del estado de cobro: no existe un
 * botón de "marcar como pagada" que escriba el estado directamente. Para saldar
 * una factura se registra el pago que la salda, y el estado se deriva solo.
 */
export const registrarPagoSchema = z.object({
    facturaId: idSchema,
    fecha: fechaSchema,
    monto: montoSchema.refine((valor) => valor > 0, 'El monto del pago debe ser mayor a cero'),
    medio: enumDe(MEDIOS_PAGO).default('TRANSFERENCIA'),
    referencia: textoOpcional(120),
    notas: textoOpcional(1000),
});
/**
 * Registro de pago rápido (Acción 1-Clic desde la fila de la tabla).
 * Si no se especifica monto o si saldoTotal es true, el backend liquida el 100% del saldo pendiente.
 */
export const registrarPagoRapidoSchema = z.object({
    fecha: fechaOpcional,
    monto: montoOpcional,
    saldoTotal: z.boolean().default(false),
    medio: enumDe(MEDIOS_PAGO).default('TRANSFERENCIA'),
    referencia: textoOpcional(120),
    notas: textoOpcional(1000),
});
export const filtrosFacturaSchema = paginacionSchema.extend({
    busqueda: z.string().trim().max(200).optional(),
    proyectoId: idSchema.optional(),
    organismoId: idSchema.optional(),
    estado: z.string().optional(),
    /** `true` para traer solo las vencidas y no pagadas. */
    soloVencidas: z.coerce.boolean().optional(),
    /** `true` para traer solo las que tienen saldo pendiente. */
    soloPorCobrar: z.coerce.boolean().optional(),
    ordenarPor: z.enum(['vencimiento', 'saldo', 'monto', 'emision', 'numero']).optional(),
    direccionOrden: z.enum(['asc', 'desc']).optional(),
    desde: fechaOpcional,
    hasta: fechaOpcional,
});
// ─────────────────────────────────────────────────────────────
// Consultas de analítica
// ─────────────────────────────────────────────────────────────
export const consultaFlujoCajaSchema = z.object({
    desde: fechaOpcional,
    hasta: fechaOpcional,
    saldoInicial: montoSchema.default(0),
    /** `true` para incluir cuotas proyectadas además de las facturas emitidas. */
    incluirProyectado: z.coerce.boolean().default(true),
});
export const consultaAnaliticaSchema = z.object({
    desde: fechaOpcional,
    hasta: fechaOpcional,
    categoriaId: idSchema.optional(),
    organismoId: idSchema.optional(),
});
// ─────────────────────────────────────────────────────────────
// MÓDULO 7 — Costo de Servicios & Proyectos (Costos Directos)
// ─────────────────────────────────────────────────────────────
export const imputarCostoServicioSchema = crearGastoBaseSchema
    .extend({
    proyectoId: idSchema,
})
    .refine((datos) => datos.montoNeto > 0 || (datos.montoTotal ?? 0) > 0, {
    message: 'Ingresa el monto neto o el monto total del costo de servicio',
    path: ['montoNeto'],
});
// ─────────────────────────────────────────────────────────────
// MÓDULO 8 — Gastos de Empresa & RRHH / Remuneraciones
// ─────────────────────────────────────────────────────────────
export const crearRemuneracionSchema = z.object({
    nombreEmpleado: textoRequerido(200, 'El nombre del colaborador'),
    rutEmpleado: textoOpcional(20),
    cargo: textoRequerido(100, 'El cargo del colaborador'),
    prevision: z.string().trim().default('FONASA'),
    sueldoBase: montoSchema,
    sueldoLiquido: montoSchema,
    montoPrevision: montoOpcional,
    bonos: montoOpcional,
    detalleBonos: textoOpcional(500),
    licitacionBonoId: idSchema.optional().nullable(),
    periodoMes: z.string().regex(/^\d{4}-\d{2}$/, 'El período debe tener formato YYYY-MM'),
    estadoPago: z.enum(['PENDIENTE', 'PAGADO']).default('PENDIENTE'),
    fechaPago: fechaOpcional,
    notas: textoOpcional(2000),
});
export const actualizarRemuneracionSchema = crearRemuneracionSchema.partial();
export const cargarNominaMesSchema = z.object({
    periodoMes: z.string().regex(/^\d{4}-\d{2}$/, 'El período debe tener formato YYYY-MM'),
    duplicarMesAnterior: z.boolean().default(true),
});
export const pagarRemuneracionSchema = z.object({
    fechaPago: fechaOpcional,
    medioPago: enumDe(MEDIOS_PAGO).optional(),
    referencia: textoOpcional(100),
});
export const asignarBonoLicitacionSchema = z.object({
    licitacionId: idSchema,
    remuneracionId: idSchema.optional().nullable(),
    nombreEmpleado: textoOpcional(200),
    cargo: textoOpcional(100),
    montoBonoNeto: montoSchema,
    periodoMes: z.string().regex(/^\d{4}-\d{2}$/, 'El período debe tener formato YYYY-MM'),
    concepto: textoRequerido(300, 'El concepto o motivo del bono'),
});
export const filtrosRemuneracionSchema = paginacionSchema.extend({
    busqueda: z.string().trim().max(200).optional(),
    periodoMes: z.string().regex(/^\d{4}-\d{2}$/).optional(),
    estadoPago: z.enum(['TODOS', 'PENDIENTE', 'PAGADO']).optional(),
    prevision: z.string().trim().optional(),
});
//# sourceMappingURL=finanzas.js.map